import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getFiscalYears = createAsyncThunk(
  "FiscalCalendar/get",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload || {}).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();

      const { data } = await axios.get(
        `${API_URL.FISCAL_CALENDAR.SLUG}${queryString ? `?${queryString}` : ""}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getActiveFiscalYear = createAsyncThunk(
  "FiscalCalendar/getActive",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.FISCAL_CALENDAR.ACTIVE, { skipInterceptorError: true });
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Same active fiscal year via the no-permission lookup endpoint (date-range
// defaults for users without FiscalCalendar access). Populates the shared
// active_fiscal_year cache.
export const getActiveFiscalYearLookup = createAsyncThunk(
  "FiscalCalendar/getActiveLookup",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.FISCAL_CALENDAR.ACTIVE_LOOKUP, { skipInterceptorError: true });
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createFiscalYear = createAsyncThunk(
  "FiscalCalendar/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.FISCAL_CALENDAR.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const closeFiscalYear = createAsyncThunk(
  "FiscalCalendar/close",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.FISCAL_CALENDAR.CLOSE(payload.id),
        { closeReason: payload.closeReason, forceClose: payload.forceClose || false }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const fiscalCalendarSlice = createSlice({
  name: "fiscalCalendar",
  initialState: {
    fiscal_year_list: [],
    active_fiscal_year: null,
    active_fiscal_year_loading: false,
    active_fiscal_year_error: null,
    current_values: null,
    active_payload: null,
    loading: false,
  },
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
    },
    clearActiveFiscalYearError(state) {
      state.active_fiscal_year_error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFiscalYears.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFiscalYears.fulfilled, (state, action) => {
        state.loading = false;
        state.fiscal_year_list = action.payload.data?.list || action.payload.data || [];
      })
      .addCase(getFiscalYears.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getActiveFiscalYear.pending, (state) => {
        state.active_fiscal_year_loading = true;
        state.active_fiscal_year_error = null;
      })
      .addCase(getActiveFiscalYear.fulfilled, (state, action) => {
        state.active_fiscal_year_loading = false;
        state.active_fiscal_year = action.payload.data;
      })
      .addCase(getActiveFiscalYear.rejected, (state, action) => {
        state.active_fiscal_year_loading = false;
        state.active_fiscal_year = null;
        if (action.payload?.status === 404) {
          state.active_fiscal_year_error = "NO_ACTIVE_FY";
        } else {
          state.active_fiscal_year_error = action.payload?.message || "Error fetching active fiscal year";
        }
      })
      .addCase(getActiveFiscalYearLookup.fulfilled, (state, action) => {
        state.active_fiscal_year = action.payload.data;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  clearActiveFiscalYearError,
} = fiscalCalendarSlice.actions;
export default fiscalCalendarSlice.reducer;
